import {TicketOfficeView} from '../../../types/ticket-office'
import {TicketOfficeProps} from '../../components/TicketOffice'
import {getOrderSummary} from './OrderSummary'
import {getSeatsByType} from './seat-utils'
import {getGridView} from './GridView'
import {getTicketView} from './TicketView'
import { OpenGiftCardModalProps } from '../../components/OpenGiftCardModal'
import { syncProducts } from './product-utils'
import { formatPrice } from '../../number-utils'

export const getTicketOfficeProps = (view: TicketOfficeView): TicketOfficeProps => {
    const { state, update } = view;
    const { isGridView, isOpenGiftCardModalOpen, openGiftCardModalType, lang, giftCardTypes } = state;
    const seatsByType = getSeatsByType(state);

    // Prepare open gift card modal props if needed
    let openGiftCardModal: OpenGiftCardModalProps | undefined;
    if (isOpenGiftCardModalOpen && openGiftCardModalType) {
        // Check if we're editing an existing item (has unitPrice > 0)
        const isEditing = openGiftCardModalType.unitPrice > 0;
        
        openGiftCardModal = {
            isOpen: true,
            giftCardType: openGiftCardModalType,
            onClose: () => {
                state.isOpenGiftCardModalOpen = false;
                state.openGiftCardModalType = undefined;
                update();
            },
            onConfirm: (amount: number, quantity: number) => {
                // For open gift cards, we need to find or create an entry with the specific price
                const priceInCents = Math.round(amount * 100);
                const compositeKey = `${openGiftCardModalType.id}_${priceInCents}`;
                
                // If editing and price changed, remove the old entry
                if (isEditing && Math.round(openGiftCardModalType.unitPrice * 100) !== priceInCents) {
                    // Find and remove the old entry
                    const oldType = giftCardTypes.find((t) => 
                        t.id === openGiftCardModalType.id && 
                        Math.round(t.unitPrice * 100) === Math.round(openGiftCardModalType.unitPrice * 100)
                    );
                    if (oldType) {
                        oldType.quantity = 0;
                    }
                }
                
                // Find existing entry with same price or create new one
                let targetType = giftCardTypes.find((t) => 
                    t.id === openGiftCardModalType.id && 
                    Math.round(t.unitPrice * 100) === priceInCents
                );
                
                if (!targetType) {
                    // Create a new entry for this price variant
                    targetType = {
                        ...openGiftCardModalType,
                        unitPrice: amount,
                        quantity: 0,
                        compositeKey
                    };
                    giftCardTypes.push(targetType);
                }
                
                // Update quantity - add to existing if not editing, replace if editing
                if (isEditing) {
                    targetType.quantity = quantity;
                } else {
                    targetType.quantity += quantity;
                }
                
                // Close modal and sync
                state.isOpenGiftCardModalOpen = false;
                state.openGiftCardModalType = undefined;
                syncProducts(view);
            },
            onDelete: isEditing ? () => {
                // Find and remove the entry
                const targetType = giftCardTypes.find((t) => 
                    t.id === openGiftCardModalType.id && 
                    Math.round(t.unitPrice * 100) === Math.round(openGiftCardModalType.unitPrice * 100)
                );
                if (targetType) {
                    targetType.quantity = 0;
                }
                
                // Close modal and sync
                state.isOpenGiftCardModalOpen = false;
                state.openGiftCardModalType = undefined;
                syncProducts(view);
            } : undefined,
            formatMoney: (amount: number) => formatPrice(amount, state.currencySymbol),
            translations: {
                title: lang.openGiftCard?.title || 'Gift Card',
                amountLabel: lang.openGiftCard?.amountLabel || 'Amount',
                quantityLabel: lang.openGiftCard?.quantityLabel || 'Quantity',
                totalLabel: lang.openGiftCard?.totalLabel || 'Total',
                addToCart: lang.openGiftCard?.addToCart || 'Add to Cart',
                save: lang.common?.save || 'Save',
                cancel: lang.common?.cancel || 'Cancel',
                amountBetween: lang.openGiftCard?.amountBetween || 'Amount must be between :min and :max',
            }
        };
    }

    return {
        orderSummary: getOrderSummary(view, seatsByType),
        gridView: isGridView && getGridView(view),
        ticketView: !isGridView && getTicketView(view),
        openGiftCardModal,
    };
};
